/**
 * @prettier
 */
import {createSelector} from 'reselect';
import {Iterable} from 'immutable';

import {getReviews, getTranslations, getTravelerFeatures} from './store';
import {getBearStayReviewData, getBookingType, getDetailPageUrl} from './listing';
import {getIsMobile, getIsDesktop, getLocaleFormatted, getReviewPageSize} from './system';
import {labelMaker} from '../../server/utils/labelMaker';
import {isReviewsModalOpen} from './modalStates';
import {convertDashesToSlashes} from '../utils/dateHelper';
import DateTimeFormatCache from '../utils/dateTimeFormatCache';

const REVIEW_COUNT_MIN = 5;
const arrivalDateFormatterCache = new DateTimeFormatCache({
    year: 'numeric',
    month: 'short'
});
const createdDateFormatterCache = new DateTimeFormatCache({
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});

const getJsReviews = createSelector(
    [getReviews],
    (reviewsData) => {
        return Iterable.isIterable(reviewsData) ? reviewsData.toJS() : reviewsData;
    }
);

const getBearStayBannerLabels = createSelector(
    [getTranslations],
    (trans) => {
        return {
            title: trans.bearStayNewTitle,
            description: trans.bearStayTeaserMessage,
            readReview: trans.bearStayTeaserButton
        };
    }
);

const getBearStayReview = createSelector(
    [getTranslations, getBearStayReviewData, getIsDesktop],
    (trans, bearStayReview, isDesktop) => {
        if (!bearStayReview) {
            return null;
        }

        let date = bearStayReview.stayedDate;
        const dateObj = new Date(date.split('T')[0].replace(/-/g, '/'));
        const splittedDate = dateObj.toDateString().split(' ');
        date = `${splittedDate[1]} ${splittedDate[2]}, ${splittedDate[3]}`;
        const content = bearStayReview.stayedComments;

        return {
            labels: {
                content,
                date,
                more: 'more',
                footerText: trans.bearStayTheBear,
                title: trans.bearStayReviewTitle,
                tooltipLabelText: trans.bearStayHelpLink
            },
            tooltipOptions: {
                delayShow: isDesktop,
                tooltipCentered: true,
                tooltipContent: trans.bearStayHelpContent,
                tooltipLabelText: trans.bearStayHelpLink,
                tooltipPlacement: 'top',
                tooltipType: 'tooltip--lg'
            }
        };
    }
);

const getReviewsLabels = createSelector(
    [getJsReviews, getTranslations],
    (reviews, trans) => {
        const viewAllReviews = reviews.reviewCount
            ? labelMaker(trans.viewAllReviews, reviews.reviewCount)
            : '';
        const seeAllReviews = reviews.reviewCount
            ? labelMaker(trans.seeAllReviews, reviews.reviewCount)
            : '';
        const labels = {
            nextPageOfReviews: trans.nextPageOfReviews,
            previousPageOfReviews: trans.previousPageOfReviews,
            reviews: labelMaker(trans.reviewCount, {
                REVIEWCOUNT: reviews.reviewCount
            }),
            ownershipTransferred: trans.ownershipTransferred,
            sortBy: trans.sortBy,
            sortByRecentMost: trans.sortByRecentMost,
            sortByRecentLeast: trans.sortByRecentLeast,
            filterBy: trans.filterBy,
            noFilter: trans.noFilter,
            filterByFiveStar: trans.filterByFiveStar,
            filterByFourStar: trans.filterByFourStar,
            filterByThreeStar: trans.filterByThreeStar,
            filterByTwoStar: trans.filterByTwoStar,
            filterByOneStar: trans.filterByOneStar,
            filterByVerifiedReview: trans.filterByVerifiedReview,
            filterByWithVerifiedReview: trans.filterByWithVerifiedReview,
            filterByWithoutVerifiedReview: trans.filterByWithoutVerifiedReview,
            pagesize: trans.pagesize,
            latestReviews: trans.latestReviews,
            tenByPage: trans.tenByPage,
            twentyByPage: trans.twentyByPage,
            fiftyByPage: trans.fiftyByPage,
            of: trans.of,
            emptyReviews: trans.emptyReviews,
            by: trans.by,
            verifiedStay: trans.verifiedStay,
            submitted: trans.submitted,
            stayed: trans.stayed,
            source: trans.source,
            isHelpful: trans.isHelpful,
            yes: trans.yes,
            no: trans.no,
            more: trans.viewMore,
            less: trans.viewLess,
            readMore: trans.viewMore,
            ownersResponse: trans.ownersResponse,
            viewAllReviews,
            seeAllReviews,
            reportReviewActive: trans.reviewsReportLinkActive,
            reportReviewInactive: trans.reviewsReportLinkInactive,
            reviewsLegalDisclaimer: trans.reviewsLegalDisclaimer
        };
        return labels;
    }
);

const getReviewList = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.reviews;
    }
);

const getFormattedReviews = createSelector(
    [getReviewList, getLocaleFormatted, getTravelerFeatures],
    (reviewList, locale, travelerFeatures) => {
        const formattedReviews = reviewList.map((review) => {
            // review.arrivalDate & review.datePublished dates come as ISO string "2017-04-20T00:00:00Z"
            const arrivalDate = new Date(convertDashesToSlashes(review.arrivalDate.split('T')[0]));
            const createdDate = new Date(
                convertDashesToSlashes(review.datePublished.split('T')[0])
            );
            const arrivalDateFormatter = arrivalDateFormatterCache.getFormatter(locale);
            const createdDateFormatter = createdDateFormatterCache.getFormatter(locale);
            const formattedArrivalDate = arrivalDateFormatter.format(arrivalDate);
            const formattedCreatedDate = createdDateFormatter.format(createdDate);
            const {isReportReviewActive = true} = review;

            return Object.assign(
                {},
                review,
                {arrivalDate: formattedArrivalDate},
                {createdDate: formattedCreatedDate},
                travelerFeatures.hasReportReviewLink ? {isReportReviewActive} : {}
            );
        });
        return formattedReviews;
    }
);

const getReviewSummaryData = createSelector(
    [getJsReviews],
    (reviewsData) => {
        // Translate the data received from the API to the contract
        // our components need.
        const {
            averageRating,
            oneStarReviewCount,
            twoStarReviewCount,
            threeStarReviewCount,
            fourStarReviewCount,
            fiveStarReviewCount
        } = reviewsData;

        return {
            score: averageRating,
            totalOne: oneStarReviewCount,
            totalTwo: twoStarReviewCount,
            totalThree: threeStarReviewCount,
            totalFour: fourStarReviewCount,
            totalFive: fiveStarReviewCount
        };
    }
);

const getNumReviewsToShow = createSelector(
    [getIsMobile, getReviewPageSize, isReviewsModalOpen],
    (isMobile, reviewPageSize, reviewsModalOpen) => {
        const MOBILE_PREVIEW_COUNT = 3;

        if (isMobile && !reviewsModalOpen) {
            return MOBILE_PREVIEW_COUNT;
        }

        return reviewPageSize;
    }
);

const getWriteReviewLinkData = createSelector(
    [getBookingType, getDetailPageUrl, getTranslations, getTravelerFeatures],
    (bookingType, detailPageUrl, trans, travelerFeatures) => {
        if (!bookingType.payPerBooking && travelerFeatures.hasWriteReviews) {
            const splitDetailPageUrl = detailPageUrl.split('/');
            const index = splitDetailPageUrl.length === 2 ? 1 : 2;
            splitDetailPageUrl.splice(index, 0, 'reviews/write');

            return {
                href: splitDetailPageUrl.join('/'),
                label: trans.writeReview
            };
        }

        return null;
    }
);

const getReviewsSelector = createSelector(
    [
        getReviewsLabels,
        getReviewSummaryData,
        getJsReviews,
        getFormattedReviews,
        getNumReviewsToShow,
        getIsMobile,
        getBearStayReview,
        getTravelerFeatures,
        getWriteReviewLinkData
    ],
    (
        labels,
        reviewsSummaryData,
        reviewsData,
        formattedReviews,
        numReviewsToShow,
        isMobile,
        bearStayReview,
        travelerFeatures,
        writeReviewLinkData
    ) => {
        const page = reviewsData.pageIndex;
        const pageSize = numReviewsToShow;
        const totalReviewCount = reviewsData.reviewCount;
        const pageStartIndex = (page - 1) * pageSize;
        const pageEndIndex =
            page * pageSize <= totalReviewCount ? page * pageSize : totalReviewCount;
        const reviewsStart = pageStartIndex === 0;
        const reviewsEnd = pageEndIndex === totalReviewCount;
        const reviewsList = formattedReviews.slice(pageStartIndex, pageEndIndex);
        const previewReviewList = formattedReviews.slice(0, numReviewsToShow);
        const hasMoreReviews = numReviewsToShow < totalReviewCount;
        const fetchedAllReviews = formattedReviews.length === totalReviewCount;
        const showReportLinks = travelerFeatures.hasReportReviewLink;
        const showReviewsLegalDisclaimer = travelerFeatures.showReviewsLegalDisclaimer;

        return {
            labels,
            reviewsSummaryData,
            page,
            hasMoreReviews,
            isFetching: reviewsData.isFetching,
            totalReviewCount,
            reviewsStart,
            reviewsEnd,
            reviewsList,
            previewReviewList,
            pageStartIndex: pageStartIndex + 1,
            pageEndIndex,
            isMobile,
            pageSize,
            fetchedAllReviews,
            reviewCountMin: REVIEW_COUNT_MIN,
            bearStayReview,
            showReportLinks,
            showReviewsLegalDisclaimer,
            writeReviewLinkData
        };
    }
);

export default getReviewsSelector;
export {
    getBearStayBannerLabels,
    getReviewsLabels,
    getReviewsSelector,
    getReviewList,
    getFormattedReviews,
    getReviewSummaryData,
    getNumReviewsToShow,
    getJsReviews,
    getWriteReviewLinkData
};
